import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

const LINK = /\[([^\]]+)\]\(([^)]+)\)/g

function fill(text: string, vars?: Record<string, string>): string {
  if (!vars) return text
  return text.replace(/\{([A-Z_]+)\}/g, (m, k: string) => vars[k] ?? m)
}

/** One line of text with [label](/path) links. Internal paths use the router. */
export function InlineMd({ text, vars }: { text: string; vars?: Record<string, string> }) {
  const s = fill(text, vars)
  const out: ReactNode[] = []
  let last = 0
  for (const m of s.matchAll(LINK)) {
    const at = m.index ?? 0
    out.push(s.slice(last, at))
    const [, label, href] = m
    out.push(
      href.startsWith('/') ? (
        <Link key={at} to={href} className="py-[15px] font-medium text-ink underline decoration-line underline-offset-2">
          {label}
        </Link>
      ) : (
        <a key={at} href={href} target="_blank" rel="noopener noreferrer" className="py-[15px] font-medium text-ink underline decoration-line underline-offset-2">
          {label}
        </a>
      ),
    )
    last = at + m[0].length
  }
  out.push(s.slice(last))
  return (
    <>
      {out.map((n, i) => (
        <Fragment key={i}>{n}</Fragment>
      ))}
    </>
  )
}

/** The small markdown subset the trust pages use: ### headings, - lists, paragraphs, links. */
export function Markdown({ source, vars }: { source: string; vars?: Record<string, string> }) {
  const blocks = source.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean)
  const firstHeading = blocks.findIndex((b) => b.startsWith('### '))
  return (
    <div className="space-y-4 text-[17px] leading-[1.55] text-ink">
      {blocks.map((b, i) => {
        if (b.startsWith('### ')) {
          const text = b.slice(4)
          return i === firstHeading ? (
            <h1 key={i} className="t-display pb-2">
              {text}
            </h1>
          ) : (
            <h2 key={i} className="t-headline pt-4">
              {text}
            </h2>
          )
        }
        const lines = b.split('\n')
        if (lines.every((l) => l.startsWith('- '))) {
          return (
            <ul key={i} className="list-disc space-y-2 pl-5 marker:text-ink-3">
              {lines.map((l, j) => (
                <li key={j}>
                  <InlineMd text={l.slice(2)} vars={vars} />
                </li>
              ))}
            </ul>
          )
        }
        return (
          <p key={i}>
            <InlineMd text={b} vars={vars} />
          </p>
        )
      })}
    </div>
  )
}
